import { type FormEvent, useEffect, useRef, useState } from "react";
import { PlusIcon } from "../../components/icons";
import { Avatar, useConfirm, useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { useSaveTeamAreas, useSaveTeamSettings, useTeam, useTeamAreas, useTeamSettings } from "../../lib/queries";
import type { TeamArea } from "../../lib/types";
import { Choice } from "./LumiPage";

/** Whether advisors see everything or only what is assigned to them. Owners and admins always see all. */
export function VisibilityCard({ canManage }: { canManage: boolean }) {
  const settings = useTeamSettings().data;
  const save = useSaveTeamSettings();
  const toast = useToast();
  if (!settings) return null;
  return (
    <div className="card">
      <div className="card-body">
        <div>
          <h3>Qué ve cada asesor</h3>
          <p className="muted">Propietarios y administradores siempre ven todas las conversaciones y casos.</p>
        </div>
        <fieldset disabled={!canManage || save.isPending} className="plain-fieldset">
          <Choice
            name="agent-visibility"
            label="Los asesores ven"
            value={settings.agentVisibility}
            onChange={(v) =>
              save
                .mutateAsync({ agentVisibility: v })
                .then(() => toast("Guardado"))
                .catch(() => toast("No se pudo guardar."))
            }
            options={[
              { value: "all", label: "Todo", hint: "Todas las conversaciones y casos de la empresa" },
              {
                value: "own",
                label: "Solo lo asignado a ellos",
                hint: "Sus conversaciones y sus casos; lo demás no lo ven",
              },
            ]}
          />
        </fieldset>
      </div>
    </div>
  );
}

/**
 * Areas of the team: when Lumi hands a conversation over it names the area, and Orbis
 * gives it to the member of that area with the fewest open conversations.
 */
export function AreasCard({ canManage }: { canManage: boolean }) {
  const areas = useTeamAreas().data ?? [];
  const team = useTeam().data?.items ?? [];
  const names = new Map(team.map((m) => [m.id, m.name]));
  const [editing, setEditing] = useState<TeamArea | "new" | null>(null);
  return (
    <div className="card">
      <div className="card-body">
        <div className="page-head">
          <div>
            <h3>Áreas de atención</h3>
            <p className="muted">
              Cuando Lumi pasa una conversación, elige el área según lo que necesita el cliente y Orbis se la asigna a
              quien tenga menos conversaciones abiertas en esa área. Si ninguna calza, queda sin responsable y se avisa
              a propietarios y administradores.
            </p>
          </div>
          {canManage && (
            <button className="btn btn-primary" type="button" onClick={() => setEditing("new")}>
              <PlusIcon />
              Nueva área
            </button>
          )}
        </div>
        {areas.length === 0 ? (
          <p className="faint">Todavía no hay áreas: las conversaciones que pasa Lumi quedan sin responsable.</p>
        ) : (
          <ul className="entity-list flush">
            {areas.map((a) => (
              <li key={a.id} className="entity-row no-avatar">
                <span className="entity-main">
                  <span className="entity-name">{a.name}</span>
                  <span className="entity-text">{a.description}</span>
                  <span className="area-members">
                    {a.memberIds.length === 0 ? (
                      <span className="pill pill-warn">Sin integrantes: Lumi no la usa</span>
                    ) : (
                      a.memberIds.map((id) => (
                        <span key={id} className="area-member">
                          <Avatar name={names.get(id) ?? null} fallback="?" />
                          {names.get(id) ?? "Alguien"}
                        </span>
                      ))
                    )}
                  </span>
                </span>
                {canManage && (
                  <span className="entity-flags">
                    <button className="btn btn-sm" type="button" onClick={() => setEditing(a)}>
                      Editar
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {editing && (
        <AreaDialog area={editing === "new" ? null : editing} areas={areas} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function AreaDialog({ area, areas, onClose }: { area: TeamArea | null; areas: TeamArea[]; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const team = useTeam().data?.items ?? [];
  const save = useSaveTeamAreas();
  const confirm = useConfirm();
  const toast = useToast();
  const [name, setName] = useState(area?.name ?? "");
  const [description, setDescription] = useState(area?.description ?? "");
  const [members, setMembers] = useState<string[]>(area?.memberIds ?? []);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  async function persist(next: TeamArea[] | (Omit<TeamArea, "id"> & { id?: string })[], done: string) {
    setError(null);
    try {
      await save.mutateAsync(
        next.map(({ id, name, description, memberIds }) => ({ id, name, description, memberIds })),
      );
      toast(done);
      dialog.current?.close();
    } catch (err) {
      setError(
        err instanceof ApiError && err.message.includes("same name")
          ? "Ya hay un área con ese nombre."
          : "No se pudo guardar el área.",
      );
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const edited = { id: area?.id, name: name.trim(), description: description.trim(), memberIds: members };
    persist(area ? areas.map((a) => (a.id === area.id ? edited : a)) : [...areas, edited], "Área guardada");
  }

  async function onDelete() {
    if (!area) return;
    const ok = await confirm({
      title: `¿Eliminar el área ${area.name}?`,
      message: "Lumi deja de pasar conversaciones a esta área. Las que ya están asignadas siguen igual.",
      confirmLabel: "Eliminar",
    });
    if (ok)
      persist(
        areas.filter((a) => a.id !== area.id),
        "Área eliminada",
      );
  }

  return (
    <dialog ref={dialog} onClose={onClose} aria-labelledby="area-title" className="area-dialog">
      <form className="dialog-form" onSubmit={onSubmit}>
        <h3 id="area-title">{area ? area.name : "Nueva área"}</h3>
        <div className="field">
          <label htmlFor="area-name">Nombre</label>
          <input
            className="input"
            id="area-name"
            placeholder="Ej.: Recargas"
            required
            maxLength={60}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="area-description">Cuándo le toca</label>
          <textarea
            className="textarea"
            id="area-description"
            rows={3}
            required
            minLength={3}
            maxLength={600}
            placeholder="Ej.: Clientes que quieren comprar más mensajes de Lumi o preguntan por recargas."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <span className="faint">
            Lumi lee esto para decidir. Escríbelo como se lo explicarías a una persona nueva.
          </span>
        </div>
        <fieldset className="field area-pick">
          <legend>Quién atiende</legend>
          {team.map((m) => (
            <label key={m.id} className="check">
              <input
                type="checkbox"
                checked={members.includes(m.id)}
                onChange={(e) =>
                  setMembers((list) => (e.target.checked ? [...list, m.id] : list.filter((id) => id !== m.id)))
                }
              />
              {m.name}
            </label>
          ))}
        </fieldset>
        {error && <div className="banner banner-warn">{error}</div>}
        <div className="row">
          {area && (
            <button className="btn btn-danger points-delete" type="button" onClick={onDelete}>
              Eliminar
            </button>
          )}
          <button className="btn" type="button" onClick={() => dialog.current?.close()}>
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={save.isPending || !name.trim() || description.trim().length < 3}
          >
            {save.isPending ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </form>
    </dialog>
  );
}
